import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';
import { useSocket } from '../context/SocketContext';
import { useSupportUrl, SUPPORT_FACEBOOK_ENABLED } from '../hooks/useSupportUrl';
import { useAlert } from '../hooks/useAlert';
import { useToast } from '../context/ToastContext';
import Icon from '../components/Icon';
import { IconButton } from '../components/UI';

interface OrderEvent { id: string; status: string; note: string | null; createdAt: string }
interface OrderDetail {
  id: string;
  orderNumber: string;
  amount: number | string;
  status: string;
  reason?: string | null;
  createdAt: string;
  notes?: string | null;
  payoutTag?: string;
  source?: string;
  game?: { name: string } | null;
  paymentMethod?: { name: string; paymentTag?: string | null } | null;
}

// Only a finished-and-good order gets the brand green. Everything else reads in
// the theme's ink so it stays legible in both light and dark.
const STATUS_COLOR: Record<string, string> = {
  Verified: '#00D632',
  Completed: '#00D632',
  Paid: '#00D632',
};

function toMoney(value: number | string | null | undefined) {
  const amount = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
}

export default function OrderTracking() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { type, id: orderId } = useParams<{ type: 'topup' | 'withdraw'; id: string }>();
  const { colors } = useTheme();
  const { orderUpdateCount } = useSocket();
  const { alert } = useAlert();
  const toast = useToast();
  const supportUrl = useSupportUrl();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [events, setEvents] = useState<OrderEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    if (!type || !orderId) return;
    try {
      const res = await api.get<{ order: OrderDetail; events: OrderEvent[] }>(`/orders/${type}/${orderId}/detail`);
      setOrder(res.data.order);
      setEvents(res.data.events);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [type, orderId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (orderUpdateCount) load(); }, [orderUpdateCount, load]);

  const copyOrderNumber = async () => {
    if (!order?.orderNumber) return;
    try {
      await navigator.clipboard.writeText(order.orderNumber);
      if (navigator.vibrate) navigator.vibrate(40);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(t('ordertracking.copyFailedTitle'), t('ordertracking.copyFailedMessage'));
    }
  };

  const canCancelWalletWithdraw = type === 'withdraw' && order?.source === 'wallet' && order.status === 'Pending';
  const cancelWalletWithdraw = () => {
    if (!order) return;
    alert(
      t('ordertracking.cancel.title'),
      t('ordertracking.cancel.message', { orderNumber: order.orderNumber, amount: toMoney(order.amount) }),
      [
        { text: t('ordertracking.cancel.keep'), style: 'cancel' },
        {
          text: t('ordertracking.cancel.confirm'),
          style: 'destructive',
          onPress: async () => {
            setCancelling(true);
            try {
              await api.post(`/orders/withdraw/${order.id}/cancel`);
              await load();
            } catch (err: unknown) {
              const e = err as { response?: { data?: { message?: string } } };
              alert(t('ordertracking.cancel.failedTitle'), e.response?.data?.message || t('ordertracking.cancel.failedMessage'));
            } finally {
              setCancelling(false);
            }
          },
        },
      ],
    );
  };

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '14px 16px',
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          backgroundColor: colors.header,
          borderBottom: `1px solid ${colors.headerBorder}`,
          position: 'sticky', top: 0, zIndex: 10,
        }}
      >
        <IconButton icon="arrow-left" accessibilityLabel={t('ordertracking.back')} onPress={() => navigate(-1)} />
        <div style={{ fontSize: 18, fontWeight: 800, color: colors.text }}>{t('ordertracking.title')}</div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div style={{
            width: 36, height: 36, border: `4px solid ${colors.accentSoft}`,
            borderTopColor: colors.accentDark, borderRadius: '50%',
            animation: 'sls-spin 0.7s linear infinite',
          }} />
        </div>
      ) : !order ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 60 }}>
          <Icon name="exclamation-circle" size={32} color={colors.textMuted} />
          <div style={{ color: colors.textMuted, marginTop: 8 }}>{t('ordertracking.notFound')}</div>
        </div>
      ) : (
        <div style={{ padding: '20px 20px 48px' }}>
          {(() => {
            const color = STATUS_COLOR[order.status] || colors.text;
            return (
              <>
                <div className="sls-order-docket" style={cardStyle(colors)}>
                  <div className="sls-label" style={mutedStyle(colors)}>{t('ordertracking.orderNumber')}</div>
                  <div
                    onClick={copyOrderNumber}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      marginTop: 4, minHeight: 50, cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: 0.5, color: colors.text }}>
                      {order.orderNumber}
                    </div>
                    <div
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        backgroundColor: colors.accentSoft, padding: '5px 10px', borderRadius: 3, minHeight: 34,
                      }}
                    >
                      <Icon name={copied ? 'check' : 'copy'} size={12} color="#00D632" />
                      <span style={{ color: '#00D632', fontWeight: 700, fontSize: 14 }}>{copied ? t('ordertracking.copied') : t('ordertracking.copy')}</span>
                    </div>
                  </div>
                  <div
                    className="sls-stamp"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      backgroundColor: 'transparent',
                      marginTop: 12,
                      color,
                    }}
                  >
                    <div>{t(`status.${order.status}`, order.status)}</div>
                  </div>
                </div>

                <div className="sls-order-ledger" style={cardStyle(colors)}>
                  <Row label={t('ordertracking.amount')} colors={colors}>
                    <span className="sls-display sls-tnum" style={{ fontSize: 28, fontWeight: 800, color: colors.text }}>${toMoney(order.amount)}</span>
                  </Row>
                  {order.game?.name && <Row label={t('ordertracking.game')} colors={colors}>{order.game.name}</Row>}
                  {order.paymentMethod?.name && <Row label={t('ordertracking.method')} colors={colors}>{order.paymentMethod.name}</Row>}
                  {order.payoutTag && (
                    <Row label={t('ordertracking.payoutTag')} colors={colors}>
                      <span style={{ fontFamily: 'Courier, monospace' }}>{order.payoutTag}</span>
                    </Row>
                  )}
                  {type === 'withdraw' && order.source && (
                    <Row label={t('ordertracking.source')} colors={colors}>{order.source === 'wallet' ? t('ordertracking.sourceWallet') : t('ordertracking.sourceGame')}</Row>
                  )}
                  {order.status === 'Rejected' && order.reason && (
                    <div style={{
                      backgroundColor: colors.surfaceMuted, border: `1px solid ${colors.border}`,
                      borderRadius: 10, padding: 10, margin: '10px 0',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <Icon name="exclamation-circle" size={12} color={colors.text} />
                        <div style={{ color: colors.text, fontSize: 14, fontWeight: 800 }}>{t('ordertracking.rejectionReason')}</div>
                      </div>
                      <div style={{ color: colors.text, fontSize: 14, lineHeight: 1.5 }}>{order.reason}</div>
                    </div>
                  )}
                  <Row label={t('ordertracking.created')} colors={colors}>{new Date(order.createdAt).toLocaleString()}</Row>
                </div>

                {canCancelWalletWithdraw && (
                  <button
                    onClick={cancelWalletWithdraw}
                    disabled={cancelling}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      gap: 8, padding: 14, borderRadius: 12,
                      border: `1px solid ${colors.danger}`,
                      backgroundColor: colors.dangerSoft,
                      color: colors.dangerText,
                      fontSize: 14, fontWeight: 900,
                      minHeight: 50, marginBottom: 12,
                      cursor: cancelling ? 'default' : 'pointer',
                      opacity: cancelling ? 0.6 : 1,
                    }}
                  >
                    {cancelling ? (
                      <div style={{
                        width: 18, height: 18, border: `2px solid ${colors.dangerSoft}`,
                        borderTopColor: colors.dangerText, borderRadius: '50%',
                        animation: 'sls-spin 0.7s linear infinite',
                      }} />
                    ) : (
                      <>
                        <Icon name="times-circle" size={14} color={colors.dangerText} />
                        {t('ordertracking.cancel.button')}
                      </>
                    )}
                  </button>
                )}

                <div className="sls-label" style={{ margin: '24px 0 8px', color: colors.sectionLabel }}>
                  {t('ordertracking.timeline')}
                </div>
                <div className="sls-order-ledger" style={cardStyle(colors)}>
                  {events.length === 0 ? (
                    <div style={{ color: colors.textMuted, fontSize: 14 }}>{t('ordertracking.noEvents')}</div>
                  ) : events.map((e, idx) => {
                    const c = STATUS_COLOR[e.status] || colors.text;
                    const last = idx === events.length - 1;
                    return (
                      <div key={e.id} className="sls-entry" style={{ display: 'flex' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: 12, width: 14 }}>
                          <div style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: c, marginTop: 4 }} />
                          {!last && <div style={{ width: 2, flex: 1, marginTop: 2, backgroundColor: colors.divider }} />}
                        </div>
                        <div style={{ flex: 1, paddingBottom: last ? 0 : 18 }}>
                          <div style={{ fontSize: 14, fontWeight: 800, color: colors.text }}>{t(`status.${e.status}`, e.status)}</div>
                          {e.note && <div style={{ fontSize: 14, marginTop: 2, color: colors.textSub }}>{e.note}</div>}
                          <div style={{ fontSize: 14, marginTop: 4, color: colors.textMuted }}>
                            {new Date(e.createdAt).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <button
                    onClick={() => SUPPORT_FACEBOOK_ENABLED ? window.open(supportUrl, '_blank', 'noopener') : navigate('/faq')}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      gap: 8, padding: 14, borderRadius: 12,
                      backgroundColor: colors.accent, color: '#000000',
                      fontWeight: 800, border: 'none', cursor: 'pointer',
                      minHeight: 50, marginTop: 8,
                    }}
                  >
                    <Icon name="headset" size={14} color="#000000" />
                    {t('ordertracking.contactSupport')}
                  </button>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function Row({ label, children, colors }: { label: string; children: React.ReactNode; colors: any }) {
  return (
    <div className="sls-entry" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div className="sls-label" style={mutedStyle(colors)}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>{children}</div>
    </div>
  );
}

const cardStyle = (colors: any): React.CSSProperties => ({
  borderRadius: 0, padding: '18px 0', marginBottom: 4,
  backgroundColor: 'transparent', border: 'none', borderBottom: `2px solid ${colors.text}`,
});
const mutedStyle = (colors: any): React.CSSProperties => ({
  fontSize: 14, fontWeight: 600, color: colors.textMuted,
});
